import { useEffect, useState } from 'react';
import { CustomDialogProps } from '@/dashboard/components/Dialog/index';
import PausePolicyInfoMonthView from '@/dashboard/components/Products/policyCard/components/infoView/monthInfo';
import LogoLoading from '@/dashboard/components/loading/LogoLoading';
import PolicyModel from '@/dashboard/models/policys/PolicyModel';
import { GridItem } from '@/dashboard/mui/styledComponent';
import { usePolicyStore } from '@/dashboard/store/PolicyStore';
import { PencilIcon } from '@heroicons/react/outline';
import {
  PolicyPremiumDetailInfoResponse,
  apiGetPausePolicyPremiumDetailInfo,
  apiPausePolicy,
  apiEditPausePolicy,
  convertDayjsTimeToServerStoredTimeData,
  formatCommonDateStr,
} from '@jove-frontend/uikit';
import { LoadingButton } from '@mui/lab';
import { Box, Button, Grid, Typography } from '@mui/material';
import { Dayjs } from 'dayjs';
import toast from 'react-hot-toast';
import { PausePolicyHandleType } from './types';
import PausePolicyWarningInfo from './warningInfo';

type PausePolicyConfirmProps = CustomDialogProps & {
  policy: PolicyModel;
  info: {
    startDate: Dayjs;
    endDate: Dayjs;
  };
  back: () => void;
  pauseType?: PausePolicyHandleType;
};

const PausePolicyConfirm = (props: PausePolicyConfirmProps) => {
  const {
    info: { startDate, endDate },
    back,
    policy,
    close,
    pauseType,
  } = props;
  const [loading, setLoading] = useState(false);
  const [{ fetchPolicies }] = usePolicyStore(({ actions }) => [actions]);
  const [cardSelectMonth, setCardSelectMonth] = useState('');
  const [premiumDetailInfo, setPremiumDetailInfo] =
    useState<PolicyPremiumDetailInfoResponse>({ billing_info: [] });
  const isEditPause = pauseType === 'edit';

  /**
   * request
   */
  const getRequestParams = () => {
    return {
      policy_id: policy.id,
      pause_start_date: convertDayjsTimeToServerStoredTimeData(startDate),
      pause_end_date: convertDayjsTimeToServerStoredTimeData(endDate),
    };
  };

  // get premium info
  const requestPausePolicyPremiunInfo = async () => {
    const params = getRequestParams() as any;
    params.hideErrToast = true;

    setLoading(true);
    const [res] = await apiGetPausePolicyPremiumDetailInfo(params);
    setLoading(false);

    if (res) {
      if (res.billing_info.length > 0) {
        setCardSelectMonth(res.billing_info[0].monthText);
      }
      setPremiumDetailInfo(res);
    }
  };

  // request pause policy
  const requestEditPausePolicy = async () => {
    const params = {
      ...getRequestParams(),
      pause_id: policy?.pauses?.[0]?.id,
    };

    setLoading(true);
    const [res, err] = await apiEditPausePolicy(params);
    setLoading(false);

    if (res) {
      fetchPolicies();
      toast.success(
        `${isEditPause ? 'Update pause' : 'Pause'} policy success!`
      );
      closeAction();
    }
  };

  // request pause policy
  const requestPausePolicy = async () => {
    const params = getRequestParams();

    setLoading(true);
    const [res, err] = await apiPausePolicy(params);
    setLoading(false);

    if (res) {
      fetchPolicies();
      toast.success(
        `${isEditPause ? 'Update pause' : 'Pause'} policy success!`
      );
      closeAction();
    }
  };

  /**
   * mounted
   */
  useEffect(() => {
    requestPausePolicyPremiunInfo();
  }, []);

  /**
   * methods
   */
  const closeAction = () => {
    if (close) close();
  };

  const backAction = () => {
    if (back) back();
  };

  // click card
  const clickCardAction = (index) => {
    setCardSelectMonth(premiumDetailInfo.billing_info[index].monthText);
  };

  return (
    <Box className="relative mt-4 flex flex-col items-center">
      {loading && <LogoLoading />}

      <Box className="bg-brand-secondary/20 text-brand-secondary rounded-full p-2">
        <PencilIcon className="h-7 w-7" />
      </Box>
      <Typography
        className="text-black-secondary mt-4 text-center font-black"
        variant="h5"
      >
        {isEditPause ? 'Edit pause' : 'Pause insurance'}
      </Typography>
      <Box className="flex flex-row items-center">
        <Typography
          className="text-black-secondary text-center"
          variant="body2"
        >
          {isEditPause ? 'New pause' : 'Pause'} period :{' '}
          {formatCommonDateStr(startDate)} - {formatCommonDateStr(endDate)}
        </Typography>
        <Box className="flex flex-row items-center">
          <span className="bg-brand-primary ml-2 h-[6px] w-[6px] rounded-full border"></span>
          <Button variant="text" onClick={backAction}>
            Change dates
          </Button>
        </Box>
      </Box>
      <Grid container spacing={2} className="mt-2">
        {premiumDetailInfo.billing_info.map((item, index) => (
          <Grid
            item
            key={item.monthText}
            xs={12 / premiumDetailInfo.billing_info.length}
          >
            <GridItem>
              <PausePolicyInfoMonthView
                index={index}
                info={item}
                click={clickCardAction}
                isActive={cardSelectMonth === item.monthText}
              />
            </GridItem>
          </Grid>
        ))}
      </Grid>
      <PausePolicyWarningInfo />
      <Box className="mt-6 flex w-full flex-row justify-end">
        <Button
          className="text-brand-primary text-lg font-bold"
          variant="text"
          onClick={closeAction}
        >
          Cancel
        </Button>
        <LoadingButton
          className="ml-2 h-12 w-[190px] rounded-full text-lg font-bold"
          loading={loading}
          loadingIndicator="Loading..."
          color="primary"
          onClick={isEditPause ? requestEditPausePolicy : requestPausePolicy}
          variant="contained"
        >
          <span>{isEditPause ? 'Save changes' : 'Pause'}</span>
        </LoadingButton>
      </Box>
    </Box>
  );
};

export default PausePolicyConfirm;
